import { BusModuleSubsystem } from 'frontend/bus-module-subsystems'
import { VappType } from 'frontend/vapp-types'
import { ClientCategory } from 'frontend/client-categories'
import { ClientFunction } from 'frontend/client-functions'
import { ClientGroup } from 'frontend/client-groups'
import { Client } from 'frontend/clients'
import { Cluster } from 'frontend/clusters'
import { DriverCategory } from 'frontend/driver-categories'
import { DriverSource } from 'frontend/driver-sources'
import { Driver } from 'frontend/drivers'
import { Location } from 'frontend/locations'
import { PurposeTag } from 'frontend/purpose-tags'
import { StagingArea } from 'frontend/staging-areas'
import { VehicleCategory } from 'frontend/vehicle-categories'
import { VehicleModel } from 'frontend/vehicle-models'
import { VehicleSource } from 'frontend/vehicle-sources'
import { VehicleType } from 'frontend/vehicle-types'
import { Vehicle } from 'frontend/vehicles'
import { camelCase } from 'lodash'
import { defineAsyncComponent, markRaw } from 'vue'

import { APPROVAL_STATES, currentState, KINDS } from './index'
import CheckInOut from './shift-check-in-out.vue'
import InclusiveCheckInOut from './shift-inclusive-check-in-out.vue'

export const ExtendedShiftEidTableCell = defineAsyncComponent(
  () => import('./components/extended-shift-eid-table-cell.vue'),
)

export const FIELDS = [
  'id',
  'eid',
  'kind',
  'is_self_managed',
  'computed_seat_capacity',
  'aasm_state',
  'acceptance_aasm_state',
  'start_at',
  'end_at',
  'updated_at',
  'dedicated_client_groups',
  'purpose_tag_ids',
  'check_in_at',
  'check_out_at',
  'overridden_check_in_time',
  'overridden_check_out_time',
  'overridden_inclusive_check_in_out',
  'computed_check_in_time',
  'computed_check_out_time',
  'computed_inclusive_check_in_out',
  'acceptance_accepted_at',
  'acceptance_declined_at',
  'acceptance_not_required_at',
  'acceptance_seen_at',
  'acceptance_sent_at',
  'checked_out_at',
  'checking_in_at',
  'ended_at',
  'first_ended_at',
  'first_started_at',
  'planned_at',
  'ready_at',
  'started_at',
  'approval_state',
  'latest_depot_departure_at',
  { bus_module_subsystem: ['id', 'name'] },
  { cluster: ['id', 'name', 'time_zone'] },
  {
    client: [
      'id',
      'name',
      'surname',
      'archived_at',
      'email',
      'phone',
      'personal_assistant_email',
      'event_phone',
      { client_categories: ['id', 'name', 'group_path'] },
      { client_functions: ['id', 'name'] },
      {
        client_groups: [
          'id',
          'background_color',
          'border_color',
          'description',
          'font_color',
          'name',
        ],
      },
    ],
  },
  {
    driver: [
      'id',
      'name',
      'surname',
      'assigned_phone',
      'personal_phone',
      'primary_phone',
      { cluster: ['id', 'name', 'time_zone'] },
      { driver_category: ['id', 'name', 'background_color', 'border_color', 'font_color'] },
      { driver_source: ['id', 'name'] },
    ],
  },
  {
    vehicle: [
      'id',
      'fleet_number',
      'registration_plate',
      { cluster: ['id', 'name', 'time_zone'] },
      { vehicle_model: ['id', 'name', { vehicle_brand: ['id', 'name'] }] },
      { vehicle_type: ['id', 'name'] },
      {
        vehicle_category: ['id', 'name', 'background_color', 'border_color', 'font_color'],
      },
      { vehicle_source: ['id', 'name', 'billable', 'maintainable'] },
      { vapp_types: ['id', 'name', 'archived_at'] },
    ],
  },
  { vapp_types: ['id', 'name', 'archived_at'] },
  {
    staging_location: [
      'id',
      'name',
      'time_zone',
      { cluster: ['id', 'name', 'time_zone'] },
      { staging_areas: ['id', 'name'] },
    ],
  },
  {
    start_depot: [
      'id',
      'name',
      'time_zone',
      { cluster: ['id', 'name', 'time_zone'] },
      { staging_areas: ['id', 'name'] },
    ],
  },
  {
    end_depot: [
      'id',
      'name',
      'time_zone',
      { cluster: ['id', 'name', 'time_zone'] },
      { staging_areas: ['id', 'name'] },
    ],
  },
  {
    purpose_tags: ['id', 'name', 'background_color', 'border_color', 'font_color'],
  },
]
export const SUBSCRIPTIONS = {
  items: 'ShiftsChannel',
  clusters: 'ClustersSimpleChannel',
  drivers: 'DriversSimpleChannel',
  vehicles: 'VehiclesWithSourceDetailedChannel',
  locations: 'LocationsWithStagingAreasChannel',
  clients: 'ClientsForShiftsChannel',
  driverSources: 'DriverSourcesSimpleChannel',
  vehicleSources: 'VehicleSourcesNoPricingChannel',
  busModuleSubsystems: 'BusModuleSubsystemsSimpleChannel',
  purposeTags: 'PurposeTagsSimpleChannel',
}

export const DEFAULT_COLUMNS = [
  'eid',
  'kind',
  'checkInAtTime',
  'approvalState',
  'startAt',
  'endAt',
  'checkOutAtTime',
  'busModuleSubsystem',
  'cluster',
  'driver',
  'vehicle',
  'location',
  'dedicatedClient',
  'dedicatedClientGroups',
  'seatCapacity',
  'computedCheckInTime',
  'computedCheckOutTime',
  'computedInclusiveCheckInOut',
  'startDepot',
  'endDepot',
  'updatedAt',
]

export const COLUMNS_CONFIG = {
  eid: {
    label: 'EID',
    header: {
      sort: 'eid',
    },
    cell: {
      kind: 'simple',
      path: 'eid',
    },
  },
  extendedEid: {
    label: 'Draft shifts',
    header: {
      sort: 'eid',
    },
    cell: {
      kind: 'component',
      component: markRaw(ExtendedShiftEidTableCell),
      componentProps: data => ({
        eid: data.eid,
        itemId: data.id,
        startDepot: data.startDepot,
        endDepot: data.endDepot,
        stagingLocation: data.stagingLocation,
        shiftStartAt: data.startAt,
        shiftEndAt: data.endAt,
        timeZone: data.stagingLocation?.timeZone,
      }),
    },
  },
  kind: {
    label: 'Kind',
    header: {
      sort: 'kind',
    },
    cell: {
      kind: 'function',
      fn: s => KINDS[s.kind],
    },
  },
  isSelfManaged: {
    label: 'Self-managed',
    header: {
      sort: 'isSelfManaged',
    },
    cell: {
      kind: 'function',
      fn: s => (s.isSelfManaged ? 'Yes' : 'No'),
    },
  },
  seatCapacity: {
    label: 'Seat capacity',
    header: {
      sort: 'computedSeatCapacity',
    },
    cell: {
      kind: 'simple',
      path: 'computedSeatCapacity',
    },
  },
  currentState: {
    label: 'Current State',
    header: {
      sort: 'currentState',
    },
    cell: {
      kind: 'function',
      fn: currentState,
    },
  },

  approvalState: {
    label: 'Approval State',
    header: {
      sort: 'approvalState',
    },
    cell: {
      kind: 'function',
      fn: s => APPROVAL_STATES[s.approvalState],
    },
  },

  busModuleSubsystem: {
    label: 'Subsystem',
    header: {
      sort: 'busModuleSubsystemName',
    },
    cell: {
      kind: 'component',
      component: markRaw(BusModuleSubsystem),
      componentProps: data => ({ busModuleSubsystem: data.busModuleSubsystem }),
    },
  },

  driver: {
    label: 'Driver',
    header: {
      sort: 'driverReverseFullName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Driver),
      componentProps: item => ({
        driver: item.driver,
        detailed: true,
      }),
    },
  },

  driverName: {
    label: 'Driver name',
    header: {
      sort: 'driverName',
    },
    cell: {
      kind: 'simple',
      path: 'driver.name',
    },
  },

  driverSurname: {
    label: 'Driver surname',
    header: {
      sort: 'driverSurname',
    },
    cell: {
      kind: 'simple',
      path: 'driver.surname',
    },
  },

  driverPhoneNumber: {
    label: 'Driver phone number',
    header: {
      sort: 'driverMainPhone',
    },
    cell: {
      kind: 'component',
      component: 'phone-number',
      componentProps: item => ({
        phoneNumber: item.driver?.[camelCase(item.driver?.primaryPhone || '')],
      }),
    },
  },

  driverCategory: {
    label: 'Driver category',
    header: {
      sort: ['driverDriverCategoryName'],
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverCategory),
      componentProps: item => ({
        driverCategory: item.driver?.driverCategory,
      }),
    },
  },

  driverSource: {
    label: 'Driver source',
    header: {
      sort: ['driverDriverSourceName'],
    },
    cell: {
      kind: 'component',
      component: markRaw(DriverSource),
      componentProps: item => ({
        driverSource: item.driver?.driverSource,
      }),
    },
  },

  vehicle: {
    label: 'Vehicle',
    header: {
      sort: ['vehicleFleetNumberPrefix', 'vehicleFleetNumberInt'],
    },
    cell: {
      kind: 'component',
      component: markRaw(Vehicle),
      componentProps: item => ({
        vehicle: item.vehicle,
        detailed: true,
      }),
    },
  },

  vehicleFleetNumber: {
    label: 'Vehicle fleet number',
    header: {
      sort: ['vehicleFleetNumberPrefix', 'vehicleFleetNumberInt'],
    },
    cell: {
      kind: 'simple',
      path: 'vehicle.fleetNumber',
    },
  },

  vehicleRegistrationPlate: {
    label: 'Vehicle reg. plate',
    header: {
      sort: 'vehicleRegistrationPlate',
    },
    cell: {
      kind: 'simple',
      path: 'vehicle.registrationPlate',
    },
  },

  vehicleCluster: {
    label: 'Vehicle cluster',
    header: {
      sort: 'vehicleVehicleClusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: item => ({
        cluster: item.vehicle?.cluster,
      }),
    },
  },

  vehicleCategory: {
    label: 'Vehicle category',
    header: {
      sort: 'vehicleVehicleCategoryName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleCategory),
      componentProps: item => ({
        vehicleCategory: item.vehicle?.vehicleCategory,
      }),
    },
  },

  vehicleType: {
    label: 'Vehicle type',
    header: {
      sort: 'vehicleVehicleTypeName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleType),
      componentProps: item => ({
        vehicleType: item.vehicle?.vehicleType,
      }),
    },
  },

  vehicleModel: {
    label: 'Vehicle model',
    header: {
      sort: 'vehicleVehicleModelName',
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleModel),
      componentProps: item => ({
        vehicleModel: item.vehicle?.vehicleModel,
      }),
    },
  },

  vehicleSource: {
    label: 'Vehicle source',
    header: {
      sort: ['vehicleVehicleSourceName'],
    },
    cell: {
      kind: 'component',
      component: markRaw(VehicleSource),
      componentProps: item => ({
        vehicleSource: item.vehicle?.vehicleSource,
      }),
    },
  },

  dedicatedClient: {
    label: 'Allocated client',
    header: {
      sort: 'clientFullName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Client),
      componentProps: item => ({
        client: item.client,
        nowrap: true,
      }),
    },
  },

  dedicatedClientName: {
    label: 'Allocated client name',
    header: {
      sort: 'clientName',
    },
    cell: {
      kind: 'simple',
      path: 'client.name',
    },
  },

  clientSurname: {
    label: 'Allocated client surname',
    header: {
      sort: 'clientSurname',
    },
    cell: {
      kind: 'simple',
      path: 'client.surname',
    },
  },

  dedicatedClientGroup: {
    label: "Allocated client's client groups",
    header: {
      // sort: 'clientFullName',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.client?.clientGroups,
        limit: 3,
        component: ClientGroup,
        componentProps: cg => ({ clientGroup: cg }),
      }),
    },
  },

  dedicatedClientCategories: {
    label: "Allocated client's Group paths",
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.client?.clientCategories,
        limit: 3,
        component: ClientCategory,
        componentProps: cc => ({ clientCategory: cc }),
      }),
    },
  },
  dedicatedClientFunctions: {
    label: "Allocated client's Functions",
    header: {},
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.client?.clientFunctions,
        limit: 3,
        component: ClientFunction,
        componentProps: cf => ({ clientFunction: cf }),
      }),
    },
  },

  dedicatedClientGroups: {
    label: 'Allocated client group',
    header: {
      sort: 'dedicatedClientGroupsNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.dedicatedClientGroups,
        limit: 5,
        component: markRaw(ClientGroup),
        componentProps: c => ({ clientGroup: c }),
      }),
    },
  },

  purposeTags: {
    label: 'Purpose Tags',
    header: {
      sort: 'purposeTagsNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.purposeTags,
        limit: 5,
        component: markRaw(PurposeTag),
        componentProps: pt => ({ purposeTag: pt }),
      }),
    },
  },

  vappTypes: {
    label: 'Required VAPP Types',
    header: {
      sort: 'vappTypesNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.vappTypes,
        limit: 5,
        allowWrap: true,
        withWrapper: true,
        wrapperClass: 'd-flex flex-wrap align-items-center justify-content-start',
        mainClass: 'd-inline-flex flex-wrap align-items-center justify-content-start',
        component: markRaw(VappType),
        componentProps: vt => ({ vappType: vt }),
      }),
    },
  },


  availableVappTypes: {
    label: 'Available VAPP Types',
    header: {
      sort: 'vehicleVappTypesNameForSort',
    },
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.vehicle?.vappTypes,
        limit: 5,
        allowWrap: true,
        withWrapper: true,
        wrapperClass: 'd-flex flex-wrap align-items-center justify-content-start',
        mainClass: 'd-inline-flex flex-wrap align-items-center justify-content-start',
        component: markRaw(VappType),
        componentProps: vt => ({ vappType: vt }),
      }),
    },
  },


  // vappTypes: {
  //   label: 'VAPP Types',
  //   header: {},
  //   cell: {
  //     kind: 'function',
  //     fn: item => {
  //       const list = item.vappTypes
  //       if (!list?.length) {
  //         return ''
  //       }
  //       return list.map(vt => `${vt.name}${vt.archivedAt ? ' (archived)' : ''}`).join(', ')
  //     },
  //   },
  // },

  cluster: {
    label: 'Cluster',
    header: {
      sort: 'clusterName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Cluster),
      componentProps: item => ({
        cluster: item.cluster,
      }),
    },
  },

  stagingLocation: {
    label: 'Staging location',
    header: {
      sort: 'stagingLocationName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({
        location: item.stagingLocation,
        // nowrap: true,
        minWidth: 250,
      }),
    },
  },

  stagingAreas: {
    label: 'Staging areas',
    cell: {
      kind: 'component',
      component: 'limited-components-list',
      componentProps: item => ({
        items: item.stagingLocation?.stagingAreas,
        limit: 5,
        component: markRaw(StagingArea),
        componentProps: stagingArea => ({
          stagingArea,
          hideYes: true,
        }),
      }),
    },
  },

  startDepot: {
    label: 'Start Depot',
    header: {
      sort: 'startDepotName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({ location: item.startDepot }),
    },
  },

  endDepot: {
    label: 'End Depot',
    header: {
      sort: 'endDepotName',
    },
    cell: {
      kind: 'component',
      component: markRaw(Location),
      componentProps: item => ({ location: item.endDepot }),
    },
  },

  checkInAt: {
    label: 'Check-in datetime',
    header: {
      sort: 'checkInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkInAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkInAtTime: {
    label: 'Check-in time',
    header: {
      sort: 'checkInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkInAt,
        tz: item.stagingLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  checkInAtDate: {
    label: 'Check-in date',
    header: {
      sort: 'checkInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkInAt,
        tz: item.stagingLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  startAt: {
    label: 'Start datetime',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  startAtTime: {
    label: 'Start time',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startAt,
        tz: item.stagingLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  startAtDate: {
    label: 'Start date',
    header: {
      sort: 'startAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startAt,
        tz: item.stagingLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  endAt: {
    label: 'End datetime',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  endAtTime: {
    label: 'End time',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endAt,
        tz: item.stagingLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  endAtDate: {
    label: 'End date',
    header: {
      sort: 'endAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endAt,
        tz: item.stagingLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  checkOutAt: {
    label: 'Check-out datetime',
    header: {
      sort: 'checkOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkOutAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkOutAtTime: {
    label: 'Check-out time',
    header: {
      sort: 'checkOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkOutAt,
        tz: item.stagingLocation?.timeZone,
        format: 'timeOnly',
      }),
    },
  },

  checkOutAtDate: {
    label: 'Check-out date',
    header: {
      sort: 'checkOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkOutAt,
        tz: item.stagingLocation?.timeZone,
        format: 'dateOnly',
      }),
    },
  },

  computedCheckInTime: {
    label: 'Check-in minutes',
    header: {
      sort: 'computedCheckInTime',
    },
    cell: {
      kind: 'component',
      component: markRaw(CheckInOut),
      componentProps: shift => ({ shift, attrSuffix: 'CheckInTime' }),
    },
  },
  computedCheckOutTime: {
    label: 'Check-out minutes',
    header: {
      sort: 'computedCheckOutTime',
    },
    cell: {
      kind: 'component',
      component: markRaw(CheckInOut),
      componentProps: shift => ({ shift, attrSuffix: 'CheckOutTime' }),
    },
  },
  computedInclusiveCheckInOut: {
    label: 'CI/CO included',
    tooltip: 'check-in/check-out times are included in the length',
    header: {
      sort: 'computedInclusiveCheckInOut',
    },
    cell: {
      kind: 'component',
      component: markRaw(InclusiveCheckInOut),
      componentProps: shift => ({ shift }),
    },
  },
  latestDepotDepartureAt: {
    label: 'Latest depot departure datetime',
    header: {
      sort: 'latestDepotDepartureAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.latestDepotDepartureAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  updatedAt: {
    label: 'Updated datetime',
    header: {
      sort: 'updatedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({ t: item.updatedAt }),
    },
  },

  updatedAtTime: {
    label: 'Updated time',
    header: {
      sort: 'updatedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.updatedAt,
        format: 'timeOnly',
      }),
    },
  },

  updatedAtDate: {
    label: 'Updated date',
    header: {
      sort: 'updatedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.updatedAt,
        format: 'dateOnly',
      }),
    },
  },

  acceptanceAcceptedAt: {
    label: 'Driver accepted datetime',
    header: {
      sort: 'acceptanceAcceptedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceAcceptedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceAcceptedDate: {
    label: 'Driver accepted date',
    header: {
      sort: 'acceptanceAcceptedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceAcceptedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceAcceptedTime: {
    label: 'Driver accepted time',
    header: {
      sort: 'acceptanceAcceptedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceAcceptedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceDeclinedAt: {
    label: 'Driver declined datetime',
    header: {
      sort: 'acceptanceDeclinedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceDeclinedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceDeclinedDate: {
    label: 'Driver declined date',
    header: {
      sort: 'acceptanceDeclinedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceDeclinedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceDeclinedTime: {
    label: 'Driver declined time',
    header: {
      sort: 'acceptanceDeclinedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceDeclinedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceNotRequiredAt: {
    label: 'Acceptance process skipped datetime',
    header: {
      sort: 'acceptanceNotRequiredAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceNotRequiredAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceNotRequiredDate: {
    label: 'Acceptance process skipped date',
    header: {
      sort: 'acceptanceNotRequiredAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceNotRequiredAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceNotRequiredTime: {
    label: 'Acceptance process skipped time',
    header: {
      sort: 'acceptanceNotRequiredAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceNotRequiredAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSeenAt: {
    label: 'Driver seen acceptance datetime',
    header: {
      sort: 'acceptanceSeenAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSeenAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSeenDate: {
    label: 'Driver seen acceptance date',
    header: {
      sort: 'acceptanceSeenAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSeenAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSeenTime: {
    label: 'Driver seen acceptance time',
    header: {
      sort: 'acceptanceSeenAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSeenAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSentAt: {
    label: 'Acceptance request sent datetime',
    header: {
      sort: 'acceptanceSentAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSentAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSentDate: {
    label: 'Acceptance request sent date',
    header: {
      sort: 'acceptanceSentAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSentAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  acceptanceSentTime: {
    label: 'Acceptance request sent time',
    header: {
      sort: 'acceptanceSentAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.acceptanceSentAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkedOutAt: {
    label: 'Checked out datetime',
    header: {
      sort: 'checkedOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkedOutAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkedOutDate: {
    label: 'Checked out date',
    header: {
      sort: 'checkedOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkedOutAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkedOutTime: {
    label: 'Checked out time',
    header: {
      sort: 'checkedOutAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkedOutAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkingInAt: {
    label: 'Checking in started datetime',
    header: {
      sort: 'checkingInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkingInAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkingInDate: {
    label: 'Checking in started date',
    header: {
      sort: 'checkingInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkingInAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  checkingInTime: {
    label: 'Checking in started time',
    header: {
      sort: 'checkingInAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.checkingInAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  endedAt: {
    label: 'Ended datetime',
    header: {
      sort: 'endedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  endedDate: {
    label: 'Ended date',
    header: {
      sort: 'endedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  endedTime: {
    label: 'Ended time',
    header: {
      sort: 'endedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.endedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstEndedAt: {
    label: 'First state change to ended datetime',
    header: {
      sort: 'firstEndedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstEndedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstEndedDate: {
    label: 'First state change to ended date',
    header: {
      sort: 'firstEndedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstEndedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstEndedTime: {
    label: 'First state change to ended time',
    header: {
      sort: 'firstEndedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstEndedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstStartedAt: {
    label: 'First state change to started datetime',
    header: {
      sort: 'firstStartedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstStartedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstStartedDate: {
    label: 'First state change to started date',
    header: {
      sort: 'firstStartedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstStartedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  firstStartedTime: {
    label: 'First state change to started time',
    header: {
      sort: 'firstStartedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.firstStartedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  plannedAt: {
    label: 'Planed datetime',
    header: {
      sort: 'plannedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.plannedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  plannedDate: {
    label: 'Planed date',
    header: {
      sort: 'plannedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.plannedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  plannedTime: {
    label: 'Planed time',
    header: {
      sort: 'plannedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.plannedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  readyAt: {
    label: 'Ready datetime',
    header: {
      sort: 'readyAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.readyAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  readyDate: {
    label: 'Ready date',
    header: {
      sort: 'readyAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.readyAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  readyTime: {
    label: 'Ready time',
    header: {
      sort: 'readyAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.readyAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  startedAt: {
    label: 'Started datetime',
    header: {
      sort: 'startedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startedAt,
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  startedDate: {
    label: 'Started date',
    header: {
      sort: 'startedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startedAt,
        format: 'dateOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },

  startedTime: {
    label: 'Started time',
    header: {
      sort: 'startedAt',
    },
    cell: {
      kind: 'component',
      component: 'formatted-time',
      componentProps: item => ({
        t: item.startedAt,
        format: 'timeOnly',
        tz: item.stagingLocation?.timeZone,
      }),
    },
  },
}
